import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Text } from '@/components/ui';
import { YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { FilePreviewContent } from '@/features/artifacts';
import { CanvasExportMenu } from '@/features/canvas/CanvasExportMenu';
import { type HydratedFile } from '@/hooks/useHydratedFile';
import { cn } from '@/lib/utils';
import { isCanvasFile } from '@/utils/files';

const PREVIEW_HEIGHT_CLASS = 'h-[28rem] max-h-[60vh] min-h-[14rem]';
const WHOLE_REPORT_HEIGHT_CLASS = 'h-[78vh] min-h-[24rem]';

type ReportArtifactProps = {
  artifact: HydratedFile;
  chatId?: string;
  isWholeReport?: boolean;
  className?: string;
  onCanvasReadyChange?: (isReady: boolean) => void;
  showCanvasExport?: boolean;
};

export const ReportArtifact = observer(
  ({
    artifact,
    chatId,
    isWholeReport = false,
    className,
    onCanvasReadyChange,
    showCanvasExport = true,
  }: ReportArtifactProps) => {
    const isCanvasDocument =
      isWholeReport &&
      artifact.status === 'ready' &&
      isCanvasFile(artifact.file.contentType, artifact.file.filename);

    return (
      <div
        className={cn(
          'group relative w-full',
          isCanvasDocument
            ? 'overflow-visible bg-transparent'
            : 'overflow-hidden rounded-xl border border-border bg-surface',
          !isCanvasDocument &&
            (isWholeReport ? WHOLE_REPORT_HEIGHT_CLASS : PREVIEW_HEIGHT_CLASS),
          className
        )}
      >
        {artifact.status === 'ready' ? (
          <>
            {showCanvasExport &&
              isCanvasFile(
                artifact.file.contentType,
                artifact.file.filename
              ) && (
                <div className="absolute right-2 top-2 z-10 rounded-md bg-surface/80 opacity-0 touch:opacity-100 backdrop-blur transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <CanvasExportMenu file={artifact.file} />
                </div>
              )}
            <FilePreviewContent
              file={artifact.file}
              chatId={chatId}
              canvasDocumentMode={isCanvasDocument}
              onCanvasDocumentReadyChange={
                isCanvasDocument ? onCanvasReadyChange : undefined
              }
            />
          </>
        ) : artifact.status === 'loading' ? (
          <Skeleton variant="low-emphasis" className="size-full" />
        ) : (
          <YStack center className="size-full gap-2 p-6 text-center">
            <Icons.info className="size-4 text-foreground-subtle" />
            <Text className="text-xs text-foreground-muted">
              This artifact is no longer available.
            </Text>
          </YStack>
        )}
      </div>
    );
  }
);
